/**
 * Ciro (revenue) entry uyari degerlendirici — saf (pure) fonksiyon.
 *
 * Hem "Ciro Onay" (CiroTalepleriPage) sayfasinda yesil/turuncu chip'ler
 * olarak gosterilen yumusak uyarilar icin, hem de ileride "Ciro Ekle"
 * formunda kirmizi blocking dogrulamalar icin tek kaynak. UI tarafi i18n
 * cevirisini kendisi yapar — biz sadece anahtar (key) listesi dondururuz.
 *
 * Cagiran sayfa hangi alanlara sahip oldugunu helper'a verir; helper hicbir
 * DOM/async/i18n yan etkisi yapmaz (hata mesajlarini chip'in icine yazmak
 * dahil — bunu cagiran t() ile yapar).
 *
 * Donus tipi: string[]  (i18n KEY listesi).
 * Hicbir uyari yoksa `[REVENUE_WARNING_KEYS.uygun]` doner — page'in chip'i
 * yesil "Uygun" diye render eder.
 *
 * @param {Object} args
 * @param {Object} args.entry  Degerlendirilecek user_revenue_entries satiri.
 * @param {Object} [args.job]  entry.job (nested) — varsa job_assignment/contract.
 * @param {Object} [args.contract]  entry.contract (nested) — renewals/invoices/
 *   work_started_without_contract. entry.contract yoksa cagiran taraf
 *   entry.job.contract'i fallback olarak verebilir (bkz. enrichRevenueEntries).
 * @param {Object} [args.firm]  entry.firm (nested) — is_established/firm_carts.
 * @param {Array}  [args.otherEntriesForJob] Ayni job_id'ye ait DIGER entry'ler.
 * @param {number} [args.currentUserTeamId] Onaylayanin/Goreni team_id'si (ROOT veya kullanici).
 * @returns {string[]} i18n key listesi (REVENUE_WARNING_KEYS sabitlerinden).
 */
export const REVENUE_WARNING_KEYS = Object.freeze({
  daha_once_yazim_satis: 'userRevenueEntries.warnings.daha_once_yazim_satis',
  tutar_manuel: 'userRevenueEntries.warnings.tutar_manuel',
  uzatma_donemi: 'userRevenueEntries.warnings.uzatma_donemi',
  firma_kurulu_degil: 'userRevenueEntries.warnings.firma_kurulu_degil',
  sozlesme_yok: 'userRevenueEntries.warnings.sozlesme_yok',
  farkli_ekip: 'userRevenueEntries.warnings.farkli_ekip',
  is_kapandi: 'userRevenueEntries.warnings.is_kapandi',
  uygun: 'userRevenueEntries.warnings.uygun',
});

// KAPANMIS IS — Ibrahim (kayit 26, 11:04-12:25): "Mart'ta kapanan ise
// Haziran'da prim girmeye calisiyorlar... Girmesini engelleme, benim gorup
// onu engellemem lazim. Yan tarafta '31.12'de danismanlik sonlandirildi'
// desin." ENGEL DEGIL, UYARI.
export const KAPANMIS_IS_DURUMLARI = new Set(['KAPATILDI', 'DANISMANLIK_SONLANDIRILDI']);

/**
 * Is kapatilmis/sonlandirilmissa { durum, tarih }, degilse null. Tarih is
 * kapatilirken girilen resmi kapanis tarihi (yoksa son calisma tarihi);
 * job_details en yeniden eskiye sirali geliyor.
 */
export const jobClosure = (job) => {
  if (!job || !KAPANMIS_IS_DURUMLARI.has(job.job_status)) return null;
  let tarih = null;
  for (const d of job.job_details || []) {
    const dd = d?.detail_data || {};
    tarih = dd.officialClosingDate || dd.lastWorkDate || null;
    if (tarih) break;
  }
  return { durum: job.job_status, tarih: tarih ? String(tarih).slice(0, 10) : null };
};

/**
 * Satis cirosu ise degil SOZLESMEYE bagli: sozlesmenin islerinin HEPSI
 * kapanmissa kapanmis sayilir (bir isi acik sozlesme hala calisiyor).
 * Tarih olarak en gec kapanan alinir.
 */
export const entryClosure = (job, contractJobs) => {
  if (job) return jobClosure(job);
  const isler = (contractJobs || []).filter(Boolean);
  if (!isler.length) return null;
  const kapanislar = isler.map(jobClosure);
  if (kapanislar.some((k) => !k)) return null;
  return kapanislar.reduce((a, b) => ((b.tarih || '') > (a.tarih || '') ? b : a));
};

/**
 * Uyari anahtari PARAMETRE tasiyabiliyor: "anahtar|durum|tarih". Anahtar
 * listesine bakan kodlar (".uygun" kontrolu) bozulmasin diye dizi yapisi
 * degismedi; hucre bunu cozuyor (bkz. warningKeyText).
 */
export const closureWarningKey = (k) => `${REVENUE_WARNING_KEYS.is_kapandi}|${k.durum}|${k.tarih || ''}`;

/** Uyari anahtarini (parametreli olabilir) ekran metnine cevirir. */
export const warningKeyText = (key, t) => {
  const [anahtar, durum, tarih] = String(key).split('|');
  if (anahtar !== REVENUE_WARNING_KEYS.is_kapandi) return t(anahtar);
  const trTarih = tarih ? tarih.split('-').reverse().join('.') : null;
  return t(`${anahtar}.${durum}${trTarih ? '' : '_tarihsiz'}`, { tarih: trTarih });
};

// Proje bazli is turleri: yalnizca bunlarda projenin onayli tarihi
// otelenebilir. Kaynak `job_types.is_project_based`; eski kayitlarda
// jobs.job_type (text enum) dolu olabildigi icin ad bazli yedek liste de
// var. DB'deki proje bazli turler: TUBITAK, ULUSLARARASI, DIGER.
const PROJE_BAZLI_TUR_ADLARI = new Set(['TUBITAK', 'ULUSLARARASI', 'DIGER']);

export const isProjectBasedJob = (job) => {
  const bayrak = job?.job_type_rel?.is_project_based;
  if (typeof bayrak === 'boolean') return bayrak;
  // job_type_rel gelmediyse eski metin kolonuna bak.
  return PROJE_BAZLI_TUR_ADLARI.has(String(job?.job_type || '').toUpperCase());
};

// Projenin onayli bitis tarihi otelenmis mi: jobs.project_extensions
// JSONB'sinde en az bir kayit varsa evet. (Kayitlarin sekli:
// [{ type: 'UZATMA'|'OTELEME', startDate, endDate, ... }])
export const isProjectExtended = (job) => {
  if (!isProjectBasedJob(job)) return false;
  const uzatmalar = Array.isArray(job?.project_extensions) ? job.project_extensions : [];
  return uzatmalar.length > 0;
};

// SATIS_CIROSU veya PROJE_YAZIM_CIROSU ise — daha onceki entry'lerden
// birinin de bu iki kanaldan birine ait olmasi durumunda (a) tetiklenir.
const WRITING_SALES_CHANNELS = new Set(['SATIS_CIROSU', 'PROJE_YAZIM_CIROSU']);

export function evaluateEntryWarnings({
  entry,
  job,
  contract,
  firm,
  otherEntriesForJob,
  currentUserTeamId,
} = {}) {
  const warnings = [];

  if (!entry) {
    return [REVENUE_WARNING_KEYS.uygun];
  }

  // (a) Ayni ise daha once SATIS/YAZIM girilmis ve simdi yine SATIS/YAZIM
  // girilmek isteniyorsa: kullanici dikkat etsin.
  const others = Array.isArray(otherEntriesForJob) ? otherEntriesForJob : [];
  const isCurrentWritingOrSales = WRITING_SALES_CHANNELS.has(entry.ciro_kanali);
  if (isCurrentWritingOrSales) {
    const hasOtherWritingOrSales = others.some(
      (e) => WRITING_SALES_CHANNELS.has(e?.ciro_kanali),
    );
    if (hasOtherWritingOrSales) {
      warnings.push(REVENUE_WARNING_KEYS.daha_once_yazim_satis);
    }
  }

  // (b) Tutar manuel girilmis: DB flag'e bak (is_manual_amount).
  //   - CiroEklePage'de "Otomatik Doldur" basilinca false set edilir.
  //   - Kullanici tutar input'una elle her yazdiginda tekrar true olur.
  //   - Default true (transformFormData hook'ta).
  //   Eski varsayimlarin aksine ay_sayisi/kanal bakma — flag kesin.
  if (entry.is_manual_amount === true) {
    warnings.push(REVENUE_WARNING_KEYS.tutar_manuel);
  }

  // (c) PROJE uzatmasi yapilmis.
  //
  // Onceden bu uyari `contract.renewals` doluysa (sozlesme yenilemesi)
  // cikiyordu — yanlis kaynak. Sozlesme yenilemesi ile proje uzatmasi ayri
  // seyler: ilki ticari, ikincisi projenin onayli bitis tarihinin otelenmesi.
  //
  // Dogrusu: isin proje tarihleri uzatilmissa (jobs.project_extensions) ve
  // is PROJE BAZLI bir turdeyse uyar. Ar-Ge Merkezi, Tasarim Merkezi,
  // Teknopark ve TEKMER'de "uzatma donemi" diye bir kavram yok; o islerde
  // uyari hic cikmamali (job_types.is_project_based = false).
  if (isProjectExtended(job)) {
    warnings.push(REVENUE_WARNING_KEYS.uzatma_donemi);
  }

  // (d) Firma kurulu degil: is_established === false (NULL ise emin
  // degiliz, uyari verme).
  if (firm && firm.is_established === false) {
    warnings.push(REVENUE_WARNING_KEYS.firma_kurulu_degil);
  }

  // (e) Sozlesme uyarisi — iki durumdan biri yeterli:
  //   1) Entry bir ise bagli ama isin hic sozlesmesi yok (contract_id NULL).
  //   2) Bagli bir sozlesme var ama sozlesme "gelmedi" durumunda.
  //
  // DIKKAT: contracts.work_started_without_contract alan adi anlaminin
  // TERSIDIR — true = "Geldi", false = "Gelmedi". Bkz. useContracts.js
  // transformData (=== true ? 'Geldi' : 'Gelmedi') ve tableConfigurations
  // contract_received kolonu. NULL/undefined ise durum bilinmiyor demektir,
  // yanlis alarm vermemek icin sadece acikca false olan deger uyari uretir.
  //
  // Sozlesme cozumlemesi: is varsa isin kendi sozlesmesi esastir
  // (job.contract), yoksa entry'nin sozlesmesi kullanilir. SATIS_CIROSU
  // girislerinde job_id yoktur ama contract_id vardir — bu yuzden eski
  // `entry.job_id != null` sarti kaldirildi.
  const linkedContract = (entry.job_id != null ? (job?.contract || contract) : contract) || null;
  const jobHasNoContract = entry.job_id != null && job != null && job.contract_id == null;
  const contractNotReceived = linkedContract?.work_started_without_contract === false;
  if (jobHasNoContract || contractNotReceived) {
    warnings.push(REVENUE_WARNING_KEYS.sozlesme_yok);
  }

  // (f) Is farkli ekibe atanmis: job_assignment.technical_team_id !=
  // currentUserTeamId. Ikisi de bilinmiyorsa karsilastirma yapma.
  const techTeamId = job?.job_assignment?.technical_team_id;
  if (
    techTeamId != null
    && currentUserTeamId != null
    && techTeamId !== currentUserTeamId
  ) {
    warnings.push(REVENUE_WARNING_KEYS.farkli_ekip);
  }

  // Kapatilmis / danismanligi sonlandirilmis is. Satis cirosunda ise yok,
  // sozlesmenin isleri bakiliyor (contract.jobs).
  const kapanis = entryClosure(job, job ? null : contract?.jobs);
  if (kapanis) warnings.push(closureWarningKey(kapanis));

  if (warnings.length === 0) {
    warnings.push(REVENUE_WARNING_KEYS.uygun);
  }
  return warnings;
}

/**
 * Ciro Ekle formunda submit'i ENGELLEYEN (kirmizi/blocking) uyari
 * degerlendirici — pure fn. evaluateEntryWarnings'in tersine, burada
 * donen her key submit'i iptal eder. UI toast gosterip form'u korur.
 *
 * Donus: string[] — bos array ise serbest, ilk eleman gosterilir.
 *
 * @param {Object} args
 * @param {Object} args.entry  Form'dan gelen aday entry — { ciro_kanali, ... }.
 * @param {Object} [args.job]  Secili is — { job_status, job_details: [{ detail_data: { projectStartDate, projectEndDate } }] }.
 * @param {Object} [args.firm] Secili firma — { firm_carts: [...] }.
 * @param {Object} [args.computed] computeQuarterAmount sonucu — { canSubmit, reason, x, y }.
 */
const REJECTED_JOB_STATUS = 'REDDEDILDI';
const APPROVED_JOB_STATUS = 'ONAYLANDI';

export const BLOCKING_WARNING_KEYS = Object.freeze({
  unapproved_job: 'unapproved_job',
  rejected_job: 'rejected_job',
  no_project_dates: 'no_project_dates',
  period_over: 'period_over',
  no_firm_contact: 'no_firm_contact',
  duplicate_quarter_entry: 'duplicate_quarter_entry',
  no_job_status: 'no_job_status',
});

const POST_APPROVAL_CHANNEL = 'ONAY_SONRASI_CIROSU';
const UNAPPROVED_JOB_STATUSES = new Set(['YAZIMDA', 'DEGERLENDIRMEDE']);

export function evaluateBlockingWarnings({
  entry,
  job,
  firm,
  computed,
  existingEntries,
  currentEntryId,
} = {}) {
  const blockers = [];
  if (!entry) return blockers;

  // (0) Duplicate: ayni (user, iş VEYA sözleşme, kanal, yil, ceyrek) icin
  // baska bir PENDING/APPROVED kayit varsa yeni giris engellenir. Kullanici
  // mevcut kaydi "Duzenle" ile guncellemeli. UPDATE sirasinda kendi kaydi
  // haric tutulur (currentEntryId).
  if (
    Array.isArray(existingEntries)
    && entry.yil != null
    && entry.ceyrek != null
    && entry.ciro_kanali
  ) {
    const yil = Number(entry.yil);
    const ceyrek = Number(entry.ceyrek);
    // SATIS_CIROSU'nda job_id null, contract_id ile eslesme; digerlerinde
    // job_id ile eslesme.
    const isSales = entry.ciro_kanali === 'SATIS_CIROSU';
    const targetRef = isSales ? entry.contract_id : entry.job_id;
    const refKey = isSales ? 'contract_id' : 'job_id';
    if (targetRef != null) {
      const duplicate = existingEntries.find((e) => {
        if (currentEntryId != null && e?.id === currentEntryId) return false;
        if (e?.status === 'REJECTED') return false;
        if (e?.ciro_kanali !== entry.ciro_kanali) return false;
        if (Number(e?.yil) !== yil) return false;
        if (Number(e?.ceyrek) !== ceyrek) return false;
        if (e?.[refKey] !== targetRef) return false;
        return true;
      });
      if (duplicate) {
        blockers.push(BLOCKING_WARNING_KEYS.duplicate_quarter_entry);
      }
    }
  }

  // (0b) Is durumu "-" (bos) olan ise yazim cirosu girilemez. Ibrahim
  // (2026-10-26): "Is durumu '-' olanlarda yazim cirosu girilemesin."
  // Durum hic girilmemis is henuz yazima baslamis sayilmaz.
  if (
    entry.ciro_kanali === 'PROJE_YAZIM_CIROSU'
    && job
    && !String(job.job_status ?? '').trim()
  ) {
    blockers.push(BLOCKING_WARNING_KEYS.no_job_status);
  }

  // (1) YAZIMDA/DEGERLENDIRMEDE bir is icin ONAY_SONRASI ciro girilemez.
  if (
    entry.ciro_kanali === POST_APPROVAL_CHANNEL
    && job
    && UNAPPROVED_JOB_STATUSES.has(job.job_status)
  ) {
    blockers.push(BLOCKING_WARNING_KEYS.unapproved_job);
  }

  // (1b) REDDEDILDI bir is icin ONAY_SONRASI ciro girilemez — ayri toast.
  // Reddedilen isin proje tarihi zaten olmadigi icin no_project_dates
  // yerine bu daha net ve dogru bir uyari.
  if (
    entry.ciro_kanali === POST_APPROVAL_CHANNEL
    && job?.job_status === REJECTED_JOB_STATUS
  ) {
    blockers.push(BLOCKING_WARNING_KEYS.rejected_job);
  }

  // (2) Proje baslangic/bitis tarihleri — sadece ONAY_SONRASI + is ONAYLANDI
  // olan project-based isler icin. Diger statuler icin (YAZIMDA/DEGERLENDIRMEDE/
  // REDDEDILDI) zaten ayri blocker'lar devreye giriyor, tarih kontrolu
  // gereksiz. Teknopark/Arge Merkezi gibi proje bazli olmayan isler icin
  // job_type match'i ile skip.
  //
  // Proje tarihleri job_details.detail_data JSONB icinde saklanir
  // (JobStatusUpdateModal "ONAYLANDI" statusunde projectStartDate /
  // projectEndDate'i set eder).
  if (
    entry.ciro_kanali === POST_APPROVAL_CHANNEL
    && job?.job_status === APPROVED_JOB_STATUS
  ) {
    // TUR KARARI VERITABANI BAYRAGINDAN — sabit ad listesinden DEGIL.
    //
    // Onceden `jobs.job_type` METIN kolonu normalize edilip sabit bir
    // "proje bazli olmayan turler" listesinde araniyordu. O kolon artik
    // OPSIYONEL: 1783100000000_drop_jobs_job_type_fk migration'i NOT NULL
    // kisitini kaldirdi ve yeni isler yalnizca `job_type_id` ile
    // aciliyor. Sonuc: yeni teknopark islerinde metin bos geliyor,
    // normalize edilmis anahtar '' oluyor, liste ile eslesmiyor ve is
    // "proje bazli" sayilip ASLA sahip olmayacagi proje tarihleri
    // isteniyordu. Prod verisi: 2026-08 sonrasi acilan teknopark
    // islerinin job_type alani bos, oncekilerde 'TEKNOPARK' dolu —
    // "gecen donem bu hatayi vermiyordu" bundan.
    //
    // `isProjectBasedJob` once job_type_rel.is_project_based bayragini
    // okur; sirket Ayarlar'dan yeni bir is turu eklediginde de dogru
    // calisir, sabit liste bakim gerektirmez.
    if (isProjectBasedJob(job)) {
      const details = Array.isArray(job?.job_details) ? job.job_details : [];
      const approvedDetail = details.find(
        (d) => d?.detail_data?.projectStartDate || d?.detail_data?.projectEndDate,
      );
      const projectStartDate = approvedDetail?.detail_data?.projectStartDate || null;
      const projectEndDate = approvedDetail?.detail_data?.projectEndDate || null;
      if (!projectStartDate || !projectEndDate) {
        blockers.push(BLOCKING_WARNING_KEYS.no_project_dates);
      }
    }
  }

  // (3) Prim donemi bitmis (sadece ONAY_SONRASI icin anlamli). Iki sinyal:
  // (a) computeQuarterAmount QUARTER_AFTER_END donduyse, (b) toplam x > y.
  if (entry.ciro_kanali === POST_APPROVAL_CHANNEL && computed) {
    const overEnd = computed.reason === 'QUARTER_AFTER_END';
    const overLimit = computed.x != null && computed.y != null && computed.x > computed.y;
    if (overEnd || overLimit) {
      blockers.push(BLOCKING_WARNING_KEYS.period_over);
    }
  }

  // (4) Firmanin iletisim karti bossa hicbir kanal'da ciro girilemez.
  if (firm && Array.isArray(firm.firm_carts) && firm.firm_carts.length === 0) {
    blockers.push(BLOCKING_WARNING_KEYS.no_firm_contact);
  }

  return blockers;
}

export default evaluateEntryWarnings;
